'use client';

import { useEffect, useRef, useState } from 'react';
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Transition } from '@headlessui/react';
import { IconArrowSquareDown } from '@/components/svg-symbols/icons';
import { cn } from '@/utils/cn';
import { InlineMessage } from './inline-message';
import { DropdownItem, InlineMessageType } from './types';
interface Props<TItem> {
  title?: string;
  defaultState?: DropdownItem<TItem>;
  onChange?: (state: DropdownItem<TItem>) => void;
  items: DropdownItem<TItem>[];
  disabled?: boolean;
  isLoading?: boolean;
  value?: DropdownItem<TItem>;
  placeholder?: string;
  optionListPosition?: 'up' | 'down';
  size?: 'large' | 'medium';
  inlineMessage?: InlineMessageType;
  spaceBottomStyle?: string;
  className?: string;
  blurInputAfterSelect?: boolean;
}
export function ComboboxDropdown<TItem>({
  defaultState,
  items,
  title,
  onChange,
  disabled = false,
  isLoading = false,
  placeholder = 'انتخاب کنید',
  optionListPosition,
  size = 'large',
  inlineMessage,
  spaceBottomStyle,
  className,
  blurInputAfterSelect
}: Props<TItem>) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalValue, setInternalValue] = useState<DropdownItem<TItem> | null>(defaultState || null);
  const [query, setQuery] = useState('');
  const handleStateChange = (newState: DropdownItem<TItem>) => {
    if (disabled) return;
    blurInputAfterSelect && inputRef.current?.blur();
    setInternalValue(newState);
    onChange && onChange(newState ?? undefined);
  };
  const showLoading = isLoading && !disabled;
  useEffect(() => {
    setInternalValue(defaultState || null);
  }, [defaultState]);
  const filteredItems = query === '' ? items : items.filter(item => item.label.toLowerCase().includes(query.toLowerCase()));
  const disableTextClasses = disabled ? 'text-text-lightGray' : 'text-text-black';
  return <div className={cn('flex flex-col gap-1 text-body-14 lg:text-body-16', className)} data-sentry-component="ComboboxDropdown" data-sentry-source-file="combobox-dropdown.tsx">
            {title && <span>{title}</span>}

            {showLoading && <div className='h-[42px] w-full animate-pulse rounded bg-gray-200' />}

            {!showLoading && <Combobox immediate as={'div'} value={internalValue} onChange={handleStateChange} disabled={disabled} onClose={() => setQuery('')}>
                    {({
        open
      }) => <div className='relative'>
                            <div className={cn('flex w-full items-center justify-between gap-2 rounded border px-2 outline-none', size === 'large' ? 'py-[9px]' : 'py-[7px]',
        // eslint-disable-next-line no-nested-ternary
        inlineMessage?.type === 'error' ? 'border-alert-error-dark focus-within:border-alert-error-dark' : open ? 'border-border-pink' : 'border-border-gray-medium', {
          'bg-surface-solid-50': disabled,
          'bg-surface-solid-0 focus-within:border-border-pink': inlineMessage?.type !== 'error' && !disabled
        })}>
                                <ComboboxInput className={cn('w-[inherit] outline-none', disableTextClasses)} displayValue={(item: DropdownItem<TItem>) => item?.label || ''} onChange={e => setQuery(e.target.value)} placeholder={placeholder} ref={inputRef} />

                                <ComboboxButton>
                                    <IconArrowSquareDown className={cn('h-6 w-6 transform transition-all duration-200', disableTextClasses)} />
                                </ComboboxButton>
                            </div>
                            <Transition as={'div'} enter='transition duration-100 ease-out' enterFrom='transform scale-95 opacity-0' enterTo='transform scale-100 opacity-100' leave='transition duration-75 ease-out' leaveFrom='transform scale-100 opacity-100' leaveTo='transform scale-95 opacity-0'>
                                <div className={cn('absolute z-20 mt-0 w-full rounded border border-border-pink bg-white py-0.5 pl-1 shadow-sm lg:py-1', optionListPosition === 'up' ? 'bottom-[110%]' : 'top-[110%]')}>
                                    {filteredItems.length ? <ComboboxOptions className={cn('cursor-pointer list-none overflow-auto leading-9 focus:outline-none', filteredItems.length >= 3 && 'max-h-[130px]')}>
                                            {filteredItems.map(item => <ComboboxOption key={item.value} value={item} as={'div'}>
                                                    {({
                  focus,
                  selected: itemSelected
                }) => <li className={cn(focus && 'duration-400 bg-gray-100 transition-all ease-in-out')}>
                                                            <span className={cn(itemSelected && 'font-medium', 'px-4 py-1 text-text-black')}>
                                                                {item.label}
                                                            </span>
                                                        </li>}
                                                </ComboboxOption>)}
                                        </ComboboxOptions> : <span className='inline-block w-full px-4 py-1.5 text-body-14 text-text-black lg:py-2 lg:text-body-16'>
                                            موردی یافت نشد.
                                        </span>}
                                </div>
                            </Transition>
                        </div>}
                </Combobox>}
            {inlineMessage?.message ? <InlineMessage {...inlineMessage} /> : <div className={cn('h-5 w-full', spaceBottomStyle)} />}
        </div>;
}